import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { UserPlus, Search, Loader2, CheckCircle2, AlertCircle, Calendar, CreditCard } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  credits: number;
  duration_days: number;
}

interface UserSearchResult {
  email: string;
  id: string;
}

export const ManualSubscriptionPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [transactionNote, setTransactionNote] = useState('Manual subscription by admin');

  // Fetch subscription plans
  const { data: plans = [], isLoading: plansLoading } = useQuery<SubscriptionPlan[]>(
    'subscription-plans',
    async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('credits');

      if (error) throw error;
      return data;
    }
  );

  const searchUser = async () => {
    if (!email.trim()) return;
    
    setIsSearching(true);
    setError(null);
    setSearchResults([]);
    setSelectedUser(null);

    try {
      const { data, error } = await supabase
        .rpc('search_users', { email_pattern: `%${email}%` });

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSelect = (user: UserSearchResult) => {
    setSelectedUser(user);
    setSearchResults([]);
  };

  const createSubscription = async () => {
    if (!selectedUser || !selectedPlan) {
      setError('Please select both a user and a subscription plan');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Call the RPC function directly
      const { data, error: rpcError } = await supabase.rpc('create_manual_subscription', {
        p_user_id: selectedUser.id,
        p_plan_id: selectedPlan,
        p_note: transactionNote
      });
      
      if (rpcError) throw rpcError;
      
      const plan = plans.find(p => p.id === selectedPlan);
      setSuccess(`Successfully created ${plan?.name} subscription for ${selectedUser.email}`);
      
      // Reset form
      setEmail('');
      setSelectedUser(null);
      setSelectedPlan('');
      setTransactionNote('Manual subscription by admin');
    } catch (err) {
      console.error('Error creating subscription:', err);
      if (err.code === '42883') {
        setError('Function not found. Please ensure the create_manual_subscription function exists in the database.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create subscription');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <UserPlus className="w-6 h-6 text-blue-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Manual Subscription
          </h2>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Manually add subscriptions for users who have paid via alternative methods
        </p>
      </div>

      <div className="p-6">
        <div className="max-w-xl space-y-6">
          {/* User Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search User by Email
            </label>
            <div className="relative">
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUser()}
                placeholder="Enter user email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={isSearching || isSubmitting}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <button
                onClick={searchUser}
                disabled={!email.trim() || isSearching}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {searchResults.map((user) => (
                    <li key={user.id}>
                      <button
                        onClick={() => handleUserSelect(user)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {user.email}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Selected User */}
            {selectedUser && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900 dark:text-blue-300">
                      Selected User
                    </h3>
                    <p className="mt-1 text-blue-800 dark:text-blue-200">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subscription Plan Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Subscription Plan
            </label>
            {plansLoading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="grid gap-4">
                {plans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`relative flex p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPlan === plan.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={plan.id}
                      checked={selectedPlan === plan.id}
                      onChange={() => setSelectedPlan(plan.id)}
                      className="sr-only"
                      disabled={isSubmitting}
                    />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {plan.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {plan.description}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          <CreditCard className="w-4 h-4" />
                          <span>{plan.credits} credits</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          <Calendar className="w-4 h-4" />
                          <span>{plan.duration_days} days</span>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Transaction Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Transaction Note (Optional)
            </label>
            <textarea
              value={transactionNote}
              onChange={(e) => setTransactionNote(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Add details about this manual subscription"
              disabled={isSubmitting}
            />
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={createSubscription}
            disabled={!selectedUser || !selectedPlan || isSubmitting}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating Subscription...
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5 mr-2" />
                Create Subscription
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};